import {
  APIChartWidgetAttributes,
  APIDashboardAttributes,
  APIDashboardFilterParameter,
  APIDashboardFilterSuggestionsResponse,
  APIDashboardViewStateResponse,
  APIHierarchicalDashboardFilterSuggestion,
  APIReportFilters,
  ArdoqId,
  AssetType,
  DashboardFilterValues,
  DashboardViewState,
  UnpersistedEntity,
  UnsavedBubbleChartWidgetAttributes,
  WidgetConfiguration,
  WidgetDatasource,
  WidgetGlobalFilterBindings,
  WidgetTypes,
} from '@ardoq/api-types';
import {
  deleteEntity,
  fetchEntities,
  fetchJson,
  headers,
  Method,
  renameEntity,
  toRequestOptions,
} from './api';
import { ApiResponse } from './types';
import { isArdoqError, Maybe } from '@ardoq/common-helpers';
import { fileAssetInFolder } from './fileAssetInFolder';

const deleteDashboard = deleteEntity('dashboard');

type CopyDashboardResponse = {
  message: string;
  data: { dashboard: [APIDashboardAttributes] };
};

const copy = (body: {
  id: string;
  newName: string;
  targetFolderName: Maybe<string>;
  shallowCopy: boolean;
}): ApiResponse<CopyDashboardResponse> => {
  const request = new Request(
    `/api/dashboard/copy`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson(request);
};

type PreparedDashboard = Omit<
  UnpersistedEntity<APIDashboardAttributes>,
  'name' | 'description' | 'widgets' | 'globalFilterParameters'
> & {
  widgets: (
    | WidgetConfiguration
    | UnsavedBubbleChartWidgetAttributes
    | {
        viewType: WidgetTypes.HEADER | WidgetTypes.NOTE;
        content: string;
        layout: {
          x: number;
          y: number;
          w: number;
          h: number;
          i: string;
        };
        _id?: string | undefined;
      }
    | {
        viewType: WidgetTypes.POWER_BI_IFRAME;
        url: string;
        layout: {
          x: number;
          y: number;
          w: number;
          h: number;
          i: string;
        };
        _id?: string | undefined;
      }
  )[];
  globalFilterParameters?: APIDashboardFilterParameter[];
};

const create = (
  body: PreparedDashboard
): ApiResponse<APIDashboardAttributes> => {
  const request = new Request(
    `/api/dashboard`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson<APIDashboardAttributes>(request);
};

const update = (
  id: ArdoqId,
  body: PreparedDashboard
): ApiResponse<APIDashboardAttributes> => {
  const request = new Request(
    `/api/dashboard/${id}`,
    toRequestOptions(Method.PUT, body)
  );
  return fetchJson<APIDashboardAttributes>(request);
};

/**
 * @deprecated use widgetPreviewV2 instead, this api does not respect the applied dashboard filter values
 */
const widgetPreview = (
  widgetData: WidgetConfiguration
): ApiResponse<APIChartWidgetAttributes> => {
  const request = new Request(
    `/api/dashboard/widget-preview`,
    toRequestOptions(Method.POST, widgetData)
  );
  return fetchJson<APIChartWidgetAttributes>(request);
};

const widgetPreviewV2 = (
  widgetData: WidgetConfiguration,
  globalFilterParameters: APIDashboardFilterParameter[],
  globalFilterValues?: DashboardFilterValues
): ApiResponse<APIChartWidgetAttributes> => {
  const request = new Request(
    `/api/dashboard/widget-preview-v2`,
    toRequestOptions(Method.POST, {
      globalFilters: {
        globalFilterParameters,
        ...(globalFilterValues ? { globalFilterValues } : {}),
      },
      widget: widgetData,
    })
  );
  return fetchJson<APIChartWidgetAttributes>(request);
};

const fetch = ({
  dashboardId,
  branchId = 'mainline',
  globalFilterValues,
}: {
  dashboardId: ArdoqId;
  branchId?: 'mainline' | ArdoqId;
  globalFilterValues?: DashboardFilterValues;
}): ApiResponse<APIDashboardAttributes> => {
  const url = new URL(
    `/api/dashboard/${dashboardId}/data`,
    window.location.origin
  );
  if (branchId !== 'mainline') {
    url.searchParams.set('branchId', branchId);
  }

  const request = new Request(
    url,
    globalFilterValues
      ? toRequestOptions(Method.POST, { globalFilterValues })
      : { headers }
  );
  return fetchJson(request);
};

const fetchViewState = (
  dashboardId: ArdoqId,
  stateId: ArdoqId
): ApiResponse<DashboardViewState> => {
  const request = new Request(
    `/api/entity-view-state/dashboard/${dashboardId}/${stateId}`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson<DashboardViewState>(request);
};

const generateViewState = (
  dashboardId: ArdoqId,
  dashboardFilterValues?: DashboardFilterValues
): ApiResponse<APIDashboardViewStateResponse> => {
  const request = new Request(
    `/api/entity-view-state/dashboard/${dashboardId}`,
    toRequestOptions(Method.POST, { dashboardFilterValues })
  );
  return fetchJson<APIDashboardViewStateResponse>(request);
};

type GetDashboardFilterSuggestionsParams = {
  dashboardId: ArdoqId;
  parameterId: ArdoqId;
  dashboardFilterValues?: DashboardFilterValues;
} & (
  | { isWithHierarchy: true }
  | { isWithHierarchy?: false; query: string; limit?: number }
);

function getDashboardFilterSuggestions(
  params: GetDashboardFilterSuggestionsParams & { isWithHierarchy: true }
): ApiResponse<
  APIDashboardFilterSuggestionsResponse<APIHierarchicalDashboardFilterSuggestion>
>;
function getDashboardFilterSuggestions(
  params: GetDashboardFilterSuggestionsParams & {
    isWithHierarchy?: false;
  }
): ApiResponse<APIDashboardFilterSuggestionsResponse>;
function getDashboardFilterSuggestions(
  params: GetDashboardFilterSuggestionsParams
): ApiResponse<APIDashboardFilterSuggestionsResponse> {
  const searchParams = params.isWithHierarchy
    ? new URLSearchParams({
        hierarchy: params.isWithHierarchy.toString(),
      })
    : new URLSearchParams({
        q: params.query,
        limit: (params.limit ?? 100).toString(),
      });
  const request = new Request(
    `/api/dashboard/${params.dashboardId}/filter-parameter/${params.parameterId}/suggestion?${searchParams.toString()}`,
    toRequestOptions(Method.POST, {
      globalFilterValues: params.dashboardFilterValues,
    })
  );
  return fetchJson<APIDashboardFilterSuggestionsResponse>(request);
}

type GetDashboardFilterSuggestionsWithUnsavedChangesParams = {
  dashboardId?: ArdoqId; // new dashboards might not have an id yet
  parameterId: ArdoqId;
  dashboardFilterValues?: DashboardFilterValues;
  dashboardFilterParameters: APIDashboardFilterParameter[];
  widgetsSupportDashboardFilters: Array<{
    _id: ArdoqId;
    datasource: WidgetDatasource;
    globalFilterBindings?: WidgetGlobalFilterBindings;
    filters?: APIReportFilters;
  }>;
} & (
  | { isWithHierarchy: true }
  | { isWithHierarchy?: false; query: string; limit?: number }
);

function getDashboardFilterSuggestionsWithUnsavedChanges(
  params: GetDashboardFilterSuggestionsWithUnsavedChangesParams & {
    isWithHierarchy: true;
  }
): ApiResponse<
  APIDashboardFilterSuggestionsResponse<APIHierarchicalDashboardFilterSuggestion>
>;
function getDashboardFilterSuggestionsWithUnsavedChanges(
  params: GetDashboardFilterSuggestionsWithUnsavedChangesParams & {
    isWithHierarchy?: false;
  }
): ApiResponse<APIDashboardFilterSuggestionsResponse>;
function getDashboardFilterSuggestionsWithUnsavedChanges(
  params: GetDashboardFilterSuggestionsWithUnsavedChangesParams
): ApiResponse<APIDashboardFilterSuggestionsResponse> {
  const searchParams = params.isWithHierarchy
    ? new URLSearchParams({
        hierarchy: params.isWithHierarchy.toString(),
      })
    : new URLSearchParams({
        q: params.query,
        limit: (params.limit ?? 100).toString(),
      });

  const url = params.dashboardId
    ? `/api/dashboard/${params.dashboardId}/filter-parameter/${params.parameterId}/suggestion?${searchParams.toString()}`
    : `/api/dashboard/filter-parameter/${params.parameterId}/suggestion?${searchParams.toString()}`;
  const request = new Request(
    url,
    toRequestOptions(Method.POST, {
      globalFilterValues: params.dashboardFilterValues,
      globalFilterParameters: params.dashboardFilterParameters,
      widgets: params.widgetsSupportDashboardFilters,
    })
  );
  return fetchJson<APIDashboardFilterSuggestionsResponse>(request);
}

// We can't use assetApi.createInFolder due to type incompatibility
// (different shape of `widgets` etc.)
const createInFolder = async (
  body: PreparedDashboard,
  folderId: ArdoqId
): ApiResponse<APIDashboardAttributes> => {
  const response = await create(body);
  if (isArdoqError(response)) return response;
  await fileAssetInFolder({
    assetId: response._id,
    folderId,
    assetType: AssetType.DASHBOARD,
  });
  return response;
};

export const dashboardApi = {
  fetch,
  fetchAll: fetchEntities<APIDashboardAttributes>('dashboard'),
  rename: renameEntity<APIDashboardAttributes>('dashboard'),
  delete: deleteDashboard,
  copy,
  create,
  createInFolder,
  update,
  widgetPreview,
  widgetPreviewV2,
  fetchViewState,
  generateViewState,
  getDashboardFilterSuggestions,
  getDashboardFilterSuggestionsWithUnsavedChanges,
};
